import { GAP, type Rect } from './layout'

/**
 * The gutters you can drag (Huanrui, 2026-10-16)
 * -------------------------------------------------------------
 * Every layout cuts its area into rows and columns, the gutter between them
 * always the same width. Read back from the places a layout gives, that cut
 * is a tree: a row of things side by side, each of which may be a column of
 * things one above the other, and so on down to single pictures.
 *
 * Dragging a gutter moves the edge between its two neighbours: one grows by
 * what the other gives up, and nothing else moves. The gutter itself never
 * changes width, so however the frames are sized every space on the sheet is
 * the same -- and that width can be set too (`gutter`), every gutter at once.
 *
 * What is kept is each node's shares (`splits`, by the node's path: "" for
 * the root, "0.2" for the third child of its first child), so a new gutter
 * width or a re-cut picture keeps the sizes chosen. A share list that no
 * longer fits its node (a different layout, more pictures) is ignored.
 */

export type Axis = 'x' | 'y'

/** A picture (its index among the places), or things side by side ('x') or stacked ('y'). */
export type SplitTree = number | { axis: Axis; children: SplitTree[] }

export type Splits = Record<string, number[]>

/** Too small to see what is in it: no frame is dragged smaller than this. */
export const MIN_FRAME = 60
/** The gutter widths it settles on as the slider passes near them. */
export const GUTTER_STOPS = [0, 6, 10, GAP, 20, 28, 36, 48]
export const GUTTER_MAX = 64

const EPS = 0.5

function bounds(rects: Rect[]): Rect {
  const x = Math.min(...rects.map((r) => r.x))
  const y = Math.min(...rects.map((r) => r.y))
  const right = Math.max(...rects.map((r) => r.x + r.w))
  const bottom = Math.max(...rects.map((r) => r.y + r.h))
  return { x, y, w: right - x, h: bottom - y }
}

/** The indexes in runs that do not overlap along this axis, in order. */
function runs(rects: Rect[], ids: number[], axis: Axis): number[][] {
  const start = (i: number) => (axis === 'x' ? rects[i].x : rects[i].y)
  const end = (i: number) => (axis === 'x' ? rects[i].x + rects[i].w : rects[i].y + rects[i].h)
  const sorted = [...ids].sort((a, b) => start(a) - start(b))
  const out: number[][] = []
  let reach = -Infinity
  for (const i of sorted) {
    if (out.length === 0 || start(i) >= reach - EPS) out.push([i])
    else out[out.length - 1].push(i)
    reach = Math.max(reach, end(i))
  }
  return out
}

/**
 * The cut a set of places was made by, or null where they were not cut in
 * rows and columns (then nothing on that sheet can be dragged).
 */
export function treeOf(rects: Rect[]): SplitTree | null {
  if (rects.length < 2) return null
  const walk = (ids: number[]): SplitTree | null => {
    if (ids.length === 1) return ids[0]
    for (const axis of ['x', 'y'] as const) {
      const groups = runs(rects, ids, axis)
      if (groups.length < 2) continue
      const children: SplitTree[] = []
      for (const group of groups) {
        const child = walk(group)
        if (child === null) return null
        // A run cut the same way as this one is folded into it: one row, not a row in a row.
        if (typeof child !== 'number' && child.axis === axis) children.push(...child.children)
        else children.push(child)
      }
      return { axis, children }
    }
    return null
  }
  return walk(rects.map((_, i) => i))
}

const leaves = (tree: SplitTree): number[] => (typeof tree === 'number' ? [tree] : tree.children.flatMap(leaves))

/** How far a node reaches along an axis, in the places it was read from. */
function extent(tree: SplitTree, rects: Rect[], axis: Axis): number {
  const box = bounds(leaves(tree).map((i) => rects[i]))
  return axis === 'x' ? box.w : box.h
}

/** The smallest a node can be along an axis without any frame going under MIN_FRAME. */
export function smallest(tree: SplitTree, axis: Axis, gap: number): number {
  if (typeof tree === 'number') return MIN_FRAME
  const each = tree.children.map((child) => smallest(child, axis, gap))
  return tree.axis === axis ? each.reduce((a, b) => a + b, 0) + gap * (tree.children.length - 1) : Math.max(...each)
}

/** A gutter that can be dragged: the edge between two neighbours in one node. */
export interface Divider {
  /** The node, by path. */
  path: string
  /** The gutter after this child. */
  index: number
  axis: Axis
  /** Where the gutter is: its first edge along the axis, its width, and how far it runs across. */
  at: number
  width: number
  from: number
  to: number
  /** The node's own place, and its children's sizes along the axis now. */
  region: Rect
  sizes: number[]
}

export interface Placed {
  rects: Rect[]
  dividers: Divider[]
}

/**
 * The places again, cut by the same tree inside the same outline, with
 * gutters `gap` wide and each node's shares taken from `splits` where they
 * are given (and fit), from the places as laid out where not.
 */
export function placeBySplits(rects: Rect[], tree: SplitTree, gap: number, splits: Splits): Placed {
  const out: Rect[] = rects.map((r) => ({ ...r }))
  const dividers: Divider[] = []
  const walk = (node: SplitTree, region: Rect, path: string) => {
    if (typeof node === 'number') {
      out[node] = region
      return
    }
    const { axis, children } = node
    const length = axis === 'x' ? region.w : region.h
    const free = Math.max(0, length - gap * (children.length - 1))
    const kept = splits[path]
    const valid =
      Array.isArray(kept) && kept.length === children.length && kept.every((v) => Number.isFinite(v) && v > 0)
    const shares = valid
      ? kept
      : (() => {
          const natural = children.map((child) => extent(child, rects, axis))
          const sum = natural.reduce((a, b) => a + b, 0) || 1
          return natural.map((v) => v / sum)
        })()
    const total = shares.reduce((a, b) => a + b, 0) || 1
    const sizes = shares.map((v) => (v / total) * free)
    let at = axis === 'x' ? region.x : region.y
    children.forEach((child, i) => {
      // The last takes whatever is left, so rounding never leaves a hairline.
      const size = i === children.length - 1 ? (axis === 'x' ? region.x + region.w : region.y + region.h) - at : sizes[i]
      const place: Rect =
        axis === 'x' ? { x: at, y: region.y, w: size, h: region.h } : { x: region.x, y: at, w: region.w, h: size }
      walk(child, place, path === '' ? String(i) : `${path}.${i}`)
      at += size
      if (i < children.length - 1) {
        dividers.push({
          path,
          index: i,
          axis,
          at,
          width: gap,
          from: axis === 'x' ? region.y : region.x,
          to: axis === 'x' ? region.y + region.h : region.x + region.w,
          region,
          sizes,
        })
      }
      at += gap
    })
  }
  walk(tree, bounds(rects), '')
  return { rects: out, dividers }
}

/** The node at a path. */
export function nodeAt(tree: SplitTree, path: string): SplitTree | null {
  if (path === '') return tree
  let node: SplitTree = tree
  for (const part of path.split('.')) {
    if (typeof node === 'number') return null
    const next: SplitTree | undefined = node.children[Number(part)]
    if (next === undefined) return null
    node = next
  }
  return node
}

/** A place a dragged gutter settles on, and what it is for the guide line. */
export interface Snap {
  /** Where the gutter's first edge goes. */
  at: number
  why: 'equal' | 'fit' | 'line' | 'centre'
}
